import piexif from 'piexifjs';
import type { EditorDocument, ExportPreset, ImageMetadata, Layer } from '../types';

export const uid = () => crypto.randomUUID();

const imageCache = new Map<string, Promise<HTMLImageElement>>();

export function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function loadHtmlImage(source: string): Promise<HTMLImageElement> {
  const cached = imageCache.get(source);
  if (cached) return cached;
  const pending = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => { imageCache.delete(source); reject(new Error('无法解码这张图片')); };
    image.src = source;
  });
  imageCache.set(source, pending);
  if (imageCache.size > 12) imageCache.delete(imageCache.keys().next().value!);
  return pending;
}

export async function decodeFile(file: File): Promise<{ source: string; image: HTMLImageElement }> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  let blob: Blob = file;
  if (extension === 'heic' || extension === 'heif' || file.type.includes('heic')) {
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.96 });
    blob = Array.isArray(converted) ? converted[0] : converted;
  } else if (extension === 'tif' || extension === 'tiff') {
    const UTIF = await import('utif2');
    const buffer = await file.arrayBuffer();
    const ifds = UTIF.decode(buffer);
    if (!ifds.length) throw new Error('TIFF 文件中没有可用图像');
    UTIF.decodeImage(buffer, ifds[0]);
    const rgba = UTIF.toRGBA8(ifds[0]);
    const canvas = document.createElement('canvas');
    canvas.width = ifds[0].width;
    canvas.height = ifds[0].height;
    canvas.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(rgba), canvas.width, canvas.height), 0, 0);
    blob = await new Promise<Blob>((resolve) => canvas.toBlob(v => resolve(v!), 'image/png'));
  }
  const source = await fileToDataUrl(blob);
  return { source, image: await loadHtmlImage(source) };
}

export async function fetchSample(): Promise<File> {
  const response = await fetch('https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=1800&q=90');
  if (!response.ok) throw new Error('示例图片加载失败');
  return new File([await response.blob()], 'portrait-sample.jpg', { type: 'image/jpeg' });
}

export function canvasFilter(doc: EditorDocument): string {
  const a = doc.adjustments;
  const b = doc.beauty;
  const brightness = 100 + a.exposure * 0.8 + a.highlights * 0.16 + a.shadows * 0.12 + b.tone * 0.12 + b.eyeLight * 0.05;
  const contrast = 100 + a.contrast * 0.65 + a.clarity * 0.24 + a.dehaze * 0.18 - b.smooth * 0.08;
  const saturation = 100 + a.saturation * 0.7 + a.vibrance * 0.4 + b.makeup * 0.12;
  const sepia = Math.max(0, a.sepia + Math.max(0, a.temperature) * 0.12);
  const blur = Math.max(0, a.blur * 0.05 + b.smooth * 0.018 + b.backgroundBlur * 0.008);
  return `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) hue-rotate(${a.hue + a.tint * .12}deg) sepia(${sepia}%) grayscale(${a.grayscale}%) blur(${blur}px)`;
}

function applyLiquify(canvas: HTMLCanvasElement, doc: EditorDocument, ratio: number) {
  if (!doc.liquifyStrokes?.length) return;
  const ctx = canvas.getContext('2d')!;
  for (const stroke of doc.liquifyStrokes) {
    const radius = Math.max(4, Math.round(stroke.radius * ratio));
    const patch = document.createElement('canvas');
    patch.width = radius * 2; patch.height = radius * 2;
    const patchContext = patch.getContext('2d')!;
    const stride = Math.max(1, Math.ceil(stroke.points.length / 16));
    for (let index = stride; index < stroke.points.length; index += stride) {
      const from = stroke.points[index - stride], rawTarget = stroke.points[index];
      const to = { x: from.x + (rawTarget.x - from.x) * stroke.strength, y: from.y + (rawTarget.y - from.y) * stroke.strength };
      const fromX = from.x * ratio, fromY = from.y * ratio, toX = to.x * ratio, toY = to.y * ratio;
      if (Math.abs(toX - fromX) + Math.abs(toY - fromY) < .35) continue;
      patchContext.clearRect(0, 0, patch.width, patch.height);
      patchContext.drawImage(canvas, fromX - radius, fromY - radius, radius * 2, radius * 2, 0, 0, radius * 2, radius * 2);
      ctx.save();
      ctx.beginPath();
      ctx.arc(toX, toY, radius, 0, Math.PI * 2);
      ctx.clip();
      ctx.globalAlpha = .9;
      ctx.drawImage(patch, toX - radius, toY - radius);
      ctx.restore();
    }
  }
}

async function drawLayer(ctx: CanvasRenderingContext2D, layer: Layer, width: number, height: number) {
  if (!layer.visible) return;
  ctx.save();
  ctx.globalAlpha = layer.opacity;
  ctx.globalCompositeOperation = layer.blendMode;
  if ((layer.type === 'image' || layer.type === 'ai') && layer.source) {
    const image = await loadHtmlImage(layer.source);
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const w = image.naturalWidth * scale;
    const h = image.naturalHeight * scale;
    ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
  } else if (layer.type === 'text' && layer.text) {
    ctx.fillStyle = layer.color || '#ffffff';
    ctx.font = `600 ${layer.fontSize || 48}px Inter, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillText(layer.text, layer.x || width * .1, layer.y || height * .1);
  }
  ctx.restore();
}

export async function renderDocument(doc: EditorDocument, canvas: HTMLCanvasElement, previewMax = 1800, before = false) {
  const ratio = Math.min(1, previewMax / Math.max(doc.width, doc.height));
  canvas.width = Math.max(1, Math.round(doc.width * ratio));
  canvas.height = Math.max(1, Math.round(doc.height * ratio));
  const ctx = canvas.getContext('2d', { alpha: true })!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.translate(doc.flipX ? canvas.width : 0, doc.flipY ? canvas.height : 0);
  ctx.scale(doc.flipX ? -1 : 1, doc.flipY ? -1 : 1);
  if (!before) ctx.filter = canvasFilter(doc);
  for (const layer of doc.layers) await drawLayer(ctx, layer, canvas.width, canvas.height);
  ctx.restore();
  if (!before) {
    for (const stroke of doc.strokes) {
      if (stroke.points.length < 2) continue;
      ctx.save();
      ctx.globalCompositeOperation = stroke.erase ? 'destination-out' : 'source-over';
      ctx.strokeStyle = stroke.color;
      ctx.globalAlpha = stroke.opacity;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = stroke.size * ratio;
      ctx.beginPath();
      stroke.points.forEach((p, i) => i ? ctx.lineTo(p.x * ratio, p.y * ratio) : ctx.moveTo(p.x * ratio, p.y * ratio));
      ctx.stroke();
      ctx.restore();
    }
    applyLiquify(canvas, doc, ratio);
  }
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, body] = dataUrl.split(',');
  const mime = header.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const bytes = Uint8Array.from(atob(body), char => char.charCodeAt(0));
  return new Blob([bytes], { type: mime });
}

function canvasToBlob(canvas: HTMLCanvasElement, format: ExportPreset['format'], quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(
    blob => blob ? resolve(blob) : reject(new Error('图片编码失败')),
    format,
    quality
  ));
}

function decimalToDms(value: number): [[number, number], [number, number], [number, number]] {
  const abs = Math.abs(value);
  const degrees = Math.floor(abs);
  const minutesFloat = (abs - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  return [[degrees, 1], [minutes, 1], [Math.round((minutesFloat - minutes) * 600000), 10000]];
}

function injectJpegMetadata(dataUrl: string, metadata: ImageMetadata, policy: ExportPreset['metadataPolicy']): string {
  if (policy === 'none') return dataUrl;
  const zeroth: Record<number, unknown> = {};
  const exif: Record<number, unknown> = {};
  const gps: Record<number, unknown> = {};
  if (metadata.author) zeroth[piexif.ImageIFD.Artist] = metadata.author;
  if (metadata.copyright) zeroth[piexif.ImageIFD.Copyright] = metadata.copyright;
  if (metadata.description && policy !== 'copyright') zeroth[piexif.ImageIFD.ImageDescription] = metadata.description;
  if (metadata.capturedAt && policy !== 'copyright') exif[piexif.ExifIFD.DateTimeOriginal] = metadata.capturedAt.replace(/-/g, ':').replace('T', ' ').slice(0, 19);
  if (policy === 'all' && metadata.latitude && metadata.longitude) {
    const lat = Number(metadata.latitude), lng = Number(metadata.longitude);
    gps[piexif.GPSIFD.GPSLatitudeRef] = lat < 0 ? 'S' : 'N';
    gps[piexif.GPSIFD.GPSLatitude] = decimalToDms(lat);
    gps[piexif.GPSIFD.GPSLongitudeRef] = lng < 0 ? 'W' : 'E';
    gps[piexif.GPSIFD.GPSLongitude] = decimalToDms(lng);
  }
  try { return piexif.insert(piexif.dump({ '0th': zeroth, Exif: exif, GPS: gps, '1st': {}, thumbnail: undefined }), dataUrl); }
  catch { return dataUrl; }
}

export async function exportDocument(doc: EditorDocument, preset: ExportPreset): Promise<Blob> {
  const canvas = document.createElement('canvas');
  await renderDocument(doc, canvas, Number.POSITIVE_INFINITY);
  const crop = doc.cropRect || { x: 0, y: 0, width: 1, height: 1 };
  let sourceX = Math.round(canvas.width * crop.x), sourceY = Math.round(canvas.height * crop.y);
  let sourceWidth = Math.max(1, Math.round(canvas.width * crop.width)), sourceHeight = Math.max(1, Math.round(canvas.height * crop.height));
  let targetWidth = Math.round(sourceWidth * preset.scale);
  let targetHeight = Math.round(sourceHeight * preset.scale);
  if (preset.maxEdge > 0 && Math.max(targetWidth, targetHeight) > preset.maxEdge) {
    const ratio = preset.maxEdge / Math.max(targetWidth, targetHeight);
    targetWidth = Math.round(targetWidth * ratio);
    targetHeight = Math.round(targetHeight * ratio);
  }
  const rotated = doc.rotation % 180 !== 0;
  const output = document.createElement('canvas');
  output.width = rotated ? targetHeight : targetWidth; output.height = rotated ? targetWidth : targetHeight;
  const outputContext = output.getContext('2d')!;
  outputContext.translate(output.width / 2, output.height / 2);
  outputContext.rotate(doc.rotation * Math.PI / 180);
  outputContext.drawImage(canvas, sourceX, sourceY, sourceWidth, sourceHeight, -targetWidth / 2, -targetHeight / 2, targetWidth, targetHeight);
  let encodeSurface = output;
  const encode = (quality: number) => canvasToBlob(encodeSurface, preset.format, quality);
  let blob = await encode(preset.quality);
  if (preset.targetKB > 0 && preset.format !== 'image/png') {
    const targetBytes = preset.targetKB * 1024;
    for (let resizeAttempt = 0; resizeAttempt < 7; resizeAttempt++) {
      let low = .08, high = preset.quality;
      blob = await encode(low);
      for (let qualityAttempt = 0; qualityAttempt < 8; qualityAttempt++) {
        const mid = (low + high) / 2;
        const candidate = await encode(mid);
        if (candidate.size > targetBytes) high = mid;
        else { low = mid; blob = candidate; }
      }
      if (blob.size <= targetBytes * 1.02 || Math.min(encodeSurface.width, encodeSurface.height) < 420) break;
      const smaller = document.createElement('canvas');
      smaller.width = Math.max(1, Math.round(encodeSurface.width * .84));
      smaller.height = Math.max(1, Math.round(encodeSurface.height * .84));
      smaller.getContext('2d')!.drawImage(encodeSurface, 0, 0, smaller.width, smaller.height);
      encodeSurface = smaller;
    }
  }
  if (preset.format === 'image/jpeg' && preset.metadataPolicy !== 'none') {
    return dataUrlToBlob(injectJpegMetadata(await fileToDataUrl(blob), doc.metadata, preset.metadataPolicy));
  }
  return blob;
}

export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = name; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
